import { useState } from "react";
import { Code2, Eye } from "lucide-react";
import { cn } from "@/lib/cn";
import { htmlToPlainText, isRichSignatureHtml, signatureToHtml, adaptSignatureHtmlForAppPreview } from "@/lib/compose/composeHtml";

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
};

type EditorMode = "preview" | "html";

export function SignatureHtmlEditor({
  value,
  onChange,
  placeholder = "Collez votre signature HTML ici…",
}: Props) {
  const [mode, setMode] = useState<EditorMode>(() =>
    value.trim() && isRichSignatureHtml(value) ? "preview" : "html"
  );

  const previewHtml = signatureToHtml(adaptSignatureHtmlForAppPreview(value));
  const isEmpty = !value.trim() || htmlToPlainText(value).length === 0;
  const rich = isRichSignatureHtml(value);

  return (
    <div className="signature-html-editor overflow-hidden rounded-xl border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] shadow-[var(--mail-shadow-sm)]">
      <div className="flex items-center justify-between gap-2 border-b border-[var(--mail-border-soft)] bg-[var(--mail-bg)]/40 px-2 py-1.5">
        <div className="flex items-center gap-0.5">
          <ModeTab
            active={mode === "preview"}
            label="Aperçu"
            icon={Eye}
            onClick={() => setMode("preview")}
          />
          <ModeTab
            active={mode === "html"}
            label="Code HTML"
            icon={Code2}
            onClick={() => setMode("html")}
          />
        </div>
        <span className="hidden text-[10px] text-[var(--mail-muted)] sm:inline">
          Tableaux, images et styles inline supportés
        </span>
      </div>

      {mode === "html" ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          spellCheck={false}
          aria-label="Code HTML de la signature"
          className="signature-html-editor__code min-h-[14rem] w-full resize-y border-0 bg-[var(--mail-surface)] px-3 py-2.5 font-mono text-xs leading-relaxed text-[var(--mail-text)] outline-none placeholder:text-[var(--mail-muted)]"
        />
      ) : (
        <div className="signature-html-editor__preview-wrap">
          {isEmpty ? (
            <p className="signature-html-editor__empty px-3 py-6 text-center text-xs text-[var(--mail-muted)]">
              Aucune signature — passez en « Code HTML » pour coller votre bloc HTML.
            </p>
          ) : (
            <div
              className={cn(
                "signature-html-editor__preview mail-compose-signature mail-signature-preview-scope",
                rich && "mail-compose-signature--rich"
              )}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ModeTab({
  active,
  label,
  icon: Icon,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium transition",
        active
          ? "bg-[var(--mail-surface)] text-[var(--mail-text)] shadow-[var(--mail-shadow-sm)]"
          : "text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]"
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label}
    </button>
  );
}
